import { ReactElement, useMemo, useCallback } from 'react';
import { ListItemIcon, ListItem, ListItemText } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWarehouse as WarehouseIcon,
  faUsers as ContactIcon,
  faMapPin as AddresIcon,
} from '@fortawesome/free-solid-svg-icons';

import { useToggleMenu } from '~hooks/index';
import { PlanFeaturesTypes } from '~globals/types/enums';

import {
  PointAddressSelectorMenuProps,
  PointAddressSelectorOptionValue,
} from './types';
import {
  PointAddressSelectorMenuButton,
  PointAddressSelectorMenuContainer,
} from './styles';
import PointAddressSelectorMenuItem from './MenuItem';

const PointAddressSelectorMenu = ({
  selectedOption,
  onSelected,
}: PointAddressSelectorMenuProps): ReactElement => {
  const [anchorElMenu, openMenu, handleToggleMenu] = useToggleMenu();

  const SelectedOptionIcon = useMemo(() => {
    if (selectedOption === 'address') return AddresIcon;

    if (selectedOption === 'contact') return ContactIcon;

    return WarehouseIcon;
  }, [selectedOption]);

  const handleSelectedOption = useCallback(
    (option: PointAddressSelectorOptionValue) => {
      onSelected(option);

      handleToggleMenu();
    },
    [onSelected, handleToggleMenu],
  );

  return (
    <>
      <PointAddressSelectorMenuButton
        endIcon={<ArrowDropDownIcon />}
        ref={anchorElMenu}
        aria-controls={openMenu ? 'point-address-selector-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
        onClick={handleToggleMenu}
        disableFocusRipple
        disableRipple
        disableTouchRipple
      >
        <FontAwesomeIcon icon={SelectedOptionIcon} />
      </PointAddressSelectorMenuButton>

      <PointAddressSelectorMenuContainer
        id="point-address-selector-menu"
        anchorEl={anchorElMenu.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={openMenu}
        onClose={handleToggleMenu}
        elevation={0}
      >
        <PointAddressSelectorMenuItem
          onClick={() => handleSelectedOption('warehouse')}
        >
          <ListItem disableGutters disablePadding component="div">
            <ListItemIcon>
              <FontAwesomeIcon icon={WarehouseIcon} />
            </ListItemIcon>

            <ListItemText
              primary="Depósito"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </PointAddressSelectorMenuItem>

        <PointAddressSelectorMenuItem
          onClick={() => handleSelectedOption('contact')}
          validSubscriptionFeature={{
            validFeature: PlanFeaturesTypes.LimitNotPro,
          }}
        >
          <ListItem disableGutters disablePadding component="div">
            <ListItemIcon>
              <FontAwesomeIcon icon={ContactIcon} />
            </ListItemIcon>

            <ListItemText
              primary="Contacto"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </PointAddressSelectorMenuItem>

        <PointAddressSelectorMenuItem
          onClick={() => handleSelectedOption('address')}
          validSubscriptionFeature={{
            validFeature: PlanFeaturesTypes.LimitNotPro,
          }}
        >
          <ListItem disableGutters disablePadding component="div">
            <ListItemIcon>
              <FontAwesomeIcon icon={AddresIcon} />
            </ListItemIcon>

            <ListItemText
              primary="Dirección"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </PointAddressSelectorMenuItem>
      </PointAddressSelectorMenuContainer>
    </>
  );
};

export default PointAddressSelectorMenu;
